import PropTypes from 'prop-types';
import { NavMarker } from 'react-rhumb';
import { List } from 'immutable';
import { EVENT_ENROLLMENT_FROM_SEGMENTATION_FILTERS, SELECTABLE_ENROLLMENT, SCHEDULED_ENROLLMENT } from 'automation-ui-enrollment/constants/EnrollmentView';
import SelectTriggerTypeContainer from 'automation-ui-enrollment/components/eventEnrollment/SelectTriggerTypeContainer';
import EventEnrollmentContainer from 'automation-ui-enrollment/components/eventEnrollment/EventEnrollmentContainer';
import ScheduledEnrollmentContainer from 'automation-ui-enrollment/components/scheduledEnrollment/ScheduledEnrollmentContainer';
import EnrollmentSettingsSection from 'automation-ui-canvas/containers/settings/v2/EnrollmentSettingsSection';
import { useMemo, useState } from 'react';
import useSettingsModelState from 'automation-ui-canvas/containers/settings/v2/useSettingsModelState';
import { getAssociatedListUpdate, getAssociatedListsUpdates, getClassicEnrollmentSettings, getGoalFilters, getGoalsList, getSuppressionFilters, getSuppressionList, getSuppressionLists } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import UnenrollmentSettingsSection from 'automation-ui-canvas/containers/settings/v2/UnenrollmentSettingsSection';
import { getEnrollmentList } from 'automation-ui-enrollment/utils/listFilters';

// We don't support list enrollment views since that already exists separately in canvas
import { jsx as _jsx } from "react/jsx-runtime";
const EnrollmentComponentsForView = {
  [EVENT_ENROLLMENT_FROM_SEGMENTATION_FILTERS]: EventEnrollmentContainer,
  [SELECTABLE_ENROLLMENT]: SelectTriggerTypeContainer,
  [SCHEDULED_ENROLLMENT]: ScheduledEnrollmentContainer

  // TODO: When new template enrollment is rolled out, implement the respective container
  // [TEMPLATE_ENROLLMENT]: TemplateEnrollmentContainer
};
const EnrollmentContainer = ({
  view,
  flow,
  onClose,
  updateEnrollmentCriteria,
  readOnly
}) => {
  const EnrollmentPanelComponent = EnrollmentComponentsForView[view];
  const classicEnrollmentSettings = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      return getClassicEnrollmentSettings(initialFlow);
    }
  });

  /* platform suppression filters */
  const suppressionFilters = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      if (initialFlow.isClassicWorkflow) {
        return {
          value: null
        };
      }
      return {
        value: getSuppressionFilters(initialFlow)
      };
    }
  });

  /* classic suppression lists */
  const suppressionLists = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      return {
        value: getSuppressionLists(initialFlow)
      };
    }
  });
  const goalFilters = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      /* initialize to null for platform workflows */
      if (!initialFlow.isClassicWorkflow) {
        return {
          value: null
        };
      }
      return {
        value: getGoalFilters(initialFlow)
      };
    }
  });
  const enrollmentList = useSettingsModelState({
    flow,
    initialize: initialFlow => {
      /* initialize to null for platform workflows */
      return {
        value: getEnrollmentList(initialFlow.toHybridFlowInterface())
      };
    }
  });
  const unenrollmentSetting = useMemo(() => {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ACTION_CONFIG_UNENROLLMENT_PANEL",
      children: /*#__PURE__*/_jsx(UnenrollmentSettingsSection, {
        flow: flow,
        readOnly: readOnly,
        suppressionFilters: suppressionFilters,
        goalFilters: goalFilters,
        classicEnrollmentSettings: classicEnrollmentSettings,
        suppressionLists: suppressionLists,
        enrollmentList: enrollmentList,
        isListeningSettingDisabled: {
          value: true
        } /* always disabled since list enrollment filter is not possible */,
        onChange: ({
          suppressionFilters: updatedSuppressionFilters,
          suppressionLists: updatedSuppressionLists,
          classicEnrollmentSettings: updatedClassicEnrollmentSettings,
          goalFilters: updatedGoalFilters
        }, options) => {
          suppressionLists.setSetting(updatedSuppressionLists, options);
          suppressionFilters.setSetting(updatedSuppressionFilters, options);
          classicEnrollmentSettings.setSetting(updatedClassicEnrollmentSettings, options);
          goalFilters.setSetting(updatedGoalFilters, options);
        }
      })
    });
  }, [flow, readOnly, classicEnrollmentSettings, suppressionFilters, suppressionLists, goalFilters, enrollmentList]);
  const enrollmentSettings = useMemo(() => {
    if (flow.isClassicWorkflow) {
      return /*#__PURE__*/_jsx(EnrollmentSettingsSection, {
        flow: flow,
        readOnly: readOnly,
        classicEnrollmentSettings: classicEnrollmentSettings,
        onChange: ({
          classicEnrollmentSettings: updatedClassicEnrollmentSettings,
          goalFilters: updatedGoalFilters
        }, options) => {
          classicEnrollmentSettings.setSetting(updatedClassicEnrollmentSettings, options);
          goalFilters.setSetting(updatedGoalFilters, options);
        }
      });
    }
    return undefined;
  }, [flow, readOnly, classicEnrollmentSettings, goalFilters]);
  const [shouldReenroll, setShouldReenroll] = useState(flow.shouldReenroll);
  return /*#__PURE__*/_jsx(EnrollmentPanelComponent, {
    shouldReenroll: shouldReenroll,
    setShouldReenroll: setShouldReenroll,
    readOnly: readOnly,
    flow: flow.toHybridFlowInterface(),
    onClose: onClose,
    hasSettingsChanged: classicEnrollmentSettings.hasChanged || suppressionFilters.hasChanged || goalFilters.hasChanged || suppressionLists.hasChanged,
    updateEnrollmentCriteria: (changes, ...rest) => {
      const enrollmentChanges = Object.assign({
        shouldReenroll
      }, changes);
      if (classicEnrollmentSettings.hasChanged) {
        enrollmentChanges.classicEnrollmentSettings = classicEnrollmentSettings.setting;
        classicEnrollmentSettings.onCommitChange();
      }
      if (suppressionLists.hasChanged) {
        var _suppressionLists$set;
        const listUpdates = getAssociatedListsUpdates({
          listTypes: ['SUPPRESSION_LIST'],
          existingLists: getSuppressionLists(flow) || List(),
          updatedLists: ((_suppressionLists$set = suppressionLists.setting) === null || _suppressionLists$set === void 0 ? void 0 : _suppressionLists$set.value) || List()
        });
        enrollmentChanges.associatedLists = (enrollmentChanges.associatedLists || []).concat(listUpdates);
      }
      if (suppressionFilters.hasChanged) {
        var _suppressionFilters$s;
        const listUpdate = getAssociatedListUpdate({
          existingList: getSuppressionList(flow),
          listTypes: ['SUPPRESSION_LIST'],
          updatedFilters: (_suppressionFilters$s = suppressionFilters.setting) === null || _suppressionFilters$s === void 0 ? void 0 : _suppressionFilters$s.value
        });
        enrollmentChanges.associatedLists = (enrollmentChanges.associatedLists || []).concat(listUpdate);
      }
      if (goalFilters.hasChanged) {
        var _goalFilters$setting;
        const listUpdate = getAssociatedListUpdate({
          existingList: getGoalsList(flow),
          listTypes: ['CLASSIC_GOAL_LIST'],
          updatedFilters: (_goalFilters$setting = goalFilters.setting) === null || _goalFilters$setting === void 0 ? void 0 : _goalFilters$setting.value
        });
        enrollmentChanges.associatedLists = (enrollmentChanges.associatedLists || []).concat(listUpdate);
      }
      return updateEnrollmentCriteria(Object.assign({}, enrollmentChanges, {
        enrollmentSettingsWriteSource: {
          goalCriteria: 'ASSOCIATED_LISTS'
        }
      }), ...rest);
    },
    settingsComponent: enrollmentSettings,
    unenrollmentComponent: unenrollmentSetting
  });
};
EnrollmentContainer.propTypes = {
  view: PropTypes.oneOf([SELECTABLE_ENROLLMENT, SELECTABLE_ENROLLMENT, SCHEDULED_ENROLLMENT, EVENT_ENROLLMENT_FROM_SEGMENTATION_FILTERS]).isRequired,
  flow: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  updateEnrollmentCriteria: PropTypes.func.isRequired
};
export default EnrollmentContainer;